import React, { FC, useRef, useCallback, useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import AddIcon from '@mui/icons-material/Add'
import { Participant } from '../../types'
import { Language } from '../../system/apitools'
import SectionWrapper from './LockedSectionWrapper'

// A frozen field renders as plain text, never a disabled input — disabled
// says "editable, just not now" and invites the question of what unlocks
// it. Static text says plainly that nothing does.
//
// Exported so other sealed sections (DiarizationSection) render the same
// idiom instead of growing a second copy of it.
export const StaticField: FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ mt: 2, mb: 1 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
      {value}
    </Typography>
  </Box>
)

interface VideoDetailsSectionProps {
  name: string
  description: string
  participants: Participant[]
  videoId: string
  onNameChange: (name: string) => void
  onDescriptionChange: (description: string) => void
  // Series and Episode are the two-line title the PLAYER shows. Name is the
  // admin-facing label and never reaches a viewer. With neither set the
  // player falls back to a single line from Name.
  series: string
  episode: string
  onSeriesChange: (series: string) => void
  onEpisodeChange: (episode: string) => void
  onParticipantsChange: (participants: Participant[]) => void
  onAvatarUpload: (pid: string, file: File) => void
  rotationAdjustment: number
  onRotationAdjustmentChange: (v: number) => void
  thumbnailUrl?: string
  onThumbnailUpload: (file: File) => void
  onCommit: () => void
  language: string
  languages: Language[]
  onLanguageChange: (lang: string) => void
  // True once this video is sealed — explicitly, or because the correction
  // window has expired; the two render identically. Freezes everything that
  // describes the video itself.
  sealed: boolean
}

const PRESET_COLORS = ['#E53935', '#1E88E5', '#43A047', '#FB8C00']
const PRESET_POSITIONS = [0, 90, 180, 270]

const avatarUrl = (videoId: string, pid: string, avatarPath: string | undefined) =>
  avatarPath ? `/api/v1/videos/${videoId}/participants/${pid}/avatar/image?v=${encodeURIComponent(avatarPath)}` : undefined

const VideoDetailsSection: FC<VideoDetailsSectionProps> = ({
  name,
  description,
  participants,
  videoId,
  onNameChange,
  onDescriptionChange,
  series,
  episode,
  onSeriesChange,
  onEpisodeChange,
  onParticipantsChange,
  onAvatarUpload,
  rotationAdjustment,
  onRotationAdjustmentChange,
  thumbnailUrl,
  onThumbnailUpload,
  onCommit,
  language,
  languages,
  onLanguageChange,
  sealed,
}) => {
  const avatarInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const thumbnailInputRef = useRef<HTMLInputElement | null>(null)
  const [rawRotation, setRawRotation] = useState(String(rotationAdjustment))
  const [rotationError, setRotationError] = useState('')

  useEffect(() => {
    setRawRotation(String(rotationAdjustment))
    setRotationError('')
  }, [rotationAdjustment])

  const updateParticipant = useCallback((pid: string, field: keyof Participant, value: any) => {
    onParticipantsChange(participants.map(p => p.id === pid ? { ...p, [field]: value } : p))
  }, [participants, onParticipantsChange])

  const addParticipant = useCallback(() => {
    const idx = participants.length
    onParticipantsChange([...participants, {
      id: crypto.randomUUID().split('-')[0],
      name: `Speaker ${idx + 1}`,
      color: PRESET_COLORS[idx % PRESET_COLORS.length],
      position: PRESET_POSITIONS[idx % PRESET_POSITIONS.length],
    }])
  }, [participants, onParticipantsChange])

  const removeParticipant = useCallback((pid: string) => {
    onParticipantsChange(participants.filter(p => p.id !== pid))
  }, [participants, onParticipantsChange])

  const moveParticipantUp = useCallback((index: number) => {
    if (index <= 0) return
    const next = [...participants]
    ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
    onParticipantsChange(next)
  }, [participants, onParticipantsChange])

  const moveParticipantDown = useCallback((index: number) => {
    if (index >= participants.length - 1) return
    const next = [...participants]
    ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
    onParticipantsChange(next)
  }, [participants, onParticipantsChange])

  const languageName = languages.find((l) => l.code === language)?.name || language

  const sealedNotice = sealed && (
    <Alert severity="info" sx={{ mb: 2 }}>
      How it identifies itself &mdash; series, episode and title &mdash; is now frozen, along
      with its description, language, participants and rotation adjustment. None of them can
      ever change again. The sealed manifest separately attests to the video&rsquo;s own media,
      so the footage cannot change either.
    </Alert>
  )

  // --- Details ---------------------------------------------------------------
  const detailsCard = (
    <SectionWrapper title="Details" status="pending">
      {sealed ? (
        <StaticField label="Name" value={name} />
      ) : (
        <TextField
          label="Name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={() => onCommit()}
          helperText="What you call this video here and in the video list. Viewers never see it."
          fullWidth
          margin="dense"
        />
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          mt: 1.5,
          p: '13px 14px',
          bgcolor: '#F8FAFB',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
        }}
      >
        <Box sx={{ flex: '0 0 200px' }}>
          {sealed ? (
            <StaticField label="Series" value={series || '—'} />
          ) : (
            <TextField
              label="Series"
              value={series}
              onChange={(e) => onSeriesChange(e.target.value)}
              onBlur={() => onCommit()}
              fullWidth
              size="small"
              sx={{ bgcolor: 'background.paper' }}
            />
          )}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {sealed ? (
            <StaticField label="Episode" value={episode || '—'} />
          ) : (
            <TextField
              label="Episode"
              value={episode}
              onChange={(e) => onEpisodeChange(e.target.value)}
              onBlur={() => onCommit()}
              helperText="Series and Episode are the title a viewer sees, one above the other. Both optional."
              fullWidth
              size="small"
              sx={{ bgcolor: 'background.paper' }}
            />
          )}
        </Box>
      </Box>

      {sealed ? (
        <StaticField label="Description" value={description || '—'} />
      ) : (
        <TextField
          label="Description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          onBlur={() => onCommit()}
          fullWidth
          margin="dense"
          multiline
          rows={3}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          {sealed ? (
            <StaticField label="Language" value={languageName} />
          ) : (
            <TextField
              label="Language"
              select
              value={language}
              onChange={(e) => onLanguageChange(e.target.value)}
              helperText="Generated subtitles are saved under this language."
              fullWidth
              margin="dense"
            >
              {languages.map((l) => (
                <MenuItem key={l.code} value={l.code}>{l.name}</MenuItem>
              ))}
            </TextField>
          )}
        </Box>
        <Box sx={{ flex: '0 0 148px' }}>
          {sealed ? (
            <StaticField label="Rotation Adj. (°)" value={rotationAdjustment} />
          ) : (
            <TextField
              label="Rotation adj. (&deg;)"
              type="text"
              value={rawRotation}
              onChange={(e) => {
                setRawRotation(e.target.value)
                setRotationError('')
              }}
              onBlur={() => {
                const v = parseFloat(rawRotation)
                if (isNaN(v)) {
                  setRotationError('-180 to 180')
                  return
                }
                const clamped = Math.min(180, Math.max(-180, v))
                setRawRotation(String(clamped))
                setRotationError('')
                onRotationAdjustmentChange(clamped)
              }}
              error={!!rotationError}
              helperText={rotationError || '-180 to 180'}
              fullWidth
              margin="dense"
            />
          )}
        </Box>
      </Box>
    </SectionWrapper>
  )

  // --- Thumbnail -------------------------------------------------------------
  const thumbnailCard = (
    <SectionWrapper title="Thumbnail" status="pending">
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {!sealed && (
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            ref={thumbnailInputRef}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) onThumbnailUpload(file)
            }}
          />
        )}
        <Box
          onClick={sealed ? undefined : () => thumbnailInputRef.current?.click()}
          sx={{
            width: 200, height: 200, flexShrink: 0, borderRadius: 1.5,
            cursor: sealed ? 'default' : 'pointer', overflow: 'hidden',
            border: thumbnailUrl ? 'none' : '2px dashed',
            borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            ...(sealed ? {} : { '&:hover': { opacity: 0.85 } }),
          }}
        >
          {thumbnailUrl ? (
            <img
              src={`/api/v1/videos/${videoId}/thumbnail/image?v=${encodeURIComponent(thumbnailUrl)}`}
              alt="Thumbnail"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 1 }}>
              {sealed ? 'No thumbnail' : 'Upload'}
            </Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Shown in the video list, and as the still frame before playback starts.
          </Typography>
          {!sealed && (
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1.5 }}
              onClick={() => thumbnailInputRef.current?.click()}
            >
              {thumbnailUrl ? 'Replace image' : 'Upload image'}
            </Button>
          )}
        </Box>
      </Box>
    </SectionWrapper>
  )

  // --- Speakers --------------------------------------------------------------
  const speakersCard = (
    <SectionWrapper
      title="Speakers"
      status="pending"
      actions={
        <>
          <Chip label={participants.length} size="small" />
          {!sealed && (
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addParticipant}>
              Add
            </Button>
          )}
        </>
      }
    >
      {participants.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No speakers yet. Add the people who appear in this video — the player places each one on
          the compass and diarisation attributes lines to them.
        </Typography>
      )}
      {participants.map((p, index) => (
        <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            sx={{
              width: 32, height: 32, borderRadius: '50%',
              backgroundColor: p.avatar_url ? 'transparent' : p.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: sealed ? 'default' : 'pointer', overflow: 'hidden', flexShrink: 0,
            }}
            onClick={sealed ? undefined : () => avatarInputRefs.current[p.id]?.click()}
          >
            {p.avatar_url ? (
              <img src={avatarUrl(videoId, p.id, p.avatar_url)} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold' }}>
                {p.name.charAt(0).toUpperCase()}
              </Typography>
            )}
          </Box>
          {sealed ? (
            <>
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }}>{p.name}</Typography>
              <Box sx={{ width: 16, height: 16, borderRadius: '4px', backgroundColor: p.color, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 46px', textAlign: 'right' }}>{p.position}&deg;</Typography>
            </>
          ) : (
            <>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={(el) => { avatarInputRefs.current[p.id] = el }}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) onAvatarUpload(p.id, file)
                }}
              />
              <TextField
                size="small"
                value={p.name}
                onChange={(e) => updateParticipant(p.id, 'name', e.target.value)}
                sx={{ flexGrow: 1, minWidth: 0 }}
              />
              <TextField
                size="small"
                type="color"
                value={p.color}
                onChange={(e) => updateParticipant(p.id, 'color', e.target.value)}
                sx={{ width: 44 }}
              />
              <TextField
                size="small"
                type="number"
                value={p.position}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v)) updateParticipant(p.id, 'position', Math.min(360, Math.max(0, v)));
                }}
                slotProps={{ htmlInput: { min: 0, max: 360, step: 1 } }}
                sx={{ width: 68 }}
              />
              <IconButton size="small" onClick={() => moveParticipantUp(index)} disabled={index === 0}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => moveParticipantDown(index)} disabled={index === participants.length - 1}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => removeParticipant(p.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      ))}
      {participants.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          The number is where the speaker sits in the 360&deg; sphere, as a compass bearing from 0 to 360&deg;.
        </Typography>
      )}
    </SectionWrapper>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sealedNotice}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
        <Box sx={{ flexGrow: 1, minWidth: 320 }}>{detailsCard}</Box>
        <Box sx={{ flex: '0 0 460px', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {thumbnailCard}
          {speakersCard}
        </Box>
      </Box>
    </Box>
  )
}

export default VideoDetailsSection
